import React, { useRef, useState } from 'react';
import { TimeEntry } from '../../interfaces/EmployeeInterface';
import { TimeEntryType } from '../../interfaces/TimesheetInterface';
import {
  PayrollEntryResult, PayrollPieceEntryBody,
} from '../../interfaces/TimesheetEditInterface';
import {
  formatClockMinutes, instantToLocalWallTime, parseClock, PUNCH_CROSSES_MIDNIGHT_CODE,
  PUNCH_CROSSES_MIDNIGHT_MESSAGE, resolveOutMinutes, wallTimeToTenantInstant, ymdLabel,
} from './clockParse';
import { EntryErrorBanner, EntryModalShell, performEntrySave, PunchErrorSlot } from './EntryModalShell';
import { PunchEchoParts } from './echoParts';

/**
 * Piece punch editor (M2-B). In/out 10-key clock fields + type (REG/MEAL —
 * WCDP meal breaks are TimeEntry rows with type 2). No job costing; payroll-
 * created punches carry no GPS and no selfie (the row's provenance marks carry
 * that explanation). Unmoved punch ends are sent verbatim so a no-op save
 * never relabels a device punch.
 */

export interface PieceCarry { type: number; }

export interface PieceEntryModalProps {
  employeeID: number;
  payPeriodID: number;
  ymd: string;
  employeeName: string;
  editSessionID: string;
  entry: TimeEntry | null;
  carry: PieceCarry | null;
  onSaved: (result: PayrollEntryResult, close: boolean, carry: PieceCarry) => void;
  onCancel: () => void;
  onConflict: (message: string) => void;
}

const PieceEntryModal: React.FC<PieceEntryModalProps> = ({
  employeeID, payPeriodID, ymd, employeeName, editSessionID,
  entry, carry, onSaved, onCancel, onConflict,
}) => {
  const isEdit = entry != null;
  const origIn = isEdit ? instantToLocalWallTime(entry!.inDateTime, entry!.inDateTimeOffset) : null;
  const origOut = isEdit && entry!.outDateTime
    ? instantToLocalWallTime(entry!.outDateTime, entry!.outDateTimeOffset)
    : null;
  const baseYmd = origIn?.ymd ?? ymd;
  const origOutMinutes = origOut != null && origIn != null
    ? (origOut.ymd > origIn.ymd ? origOut.minutes + 1440 : origOut.minutes)
    : null;

  const [type, setType] = useState<number>(isEdit ? entry!.type : carry?.type ?? TimeEntryType.Regular);
  const [inRaw, setInRaw] = useState<string>(origIn ? formatClockMinutes(origIn.minutes) : '');
  const [outRaw, setOutRaw] = useState<string>(origOutMinutes != null ? formatClockMinutes(origOutMinutes) : '');
  const [bad, setBad] = useState<'in' | 'out' | null>(null);
  const [banner, setBanner] = useState<{ title: string; body?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  /** Cross-midnight refusal in the fixed slot under In / Out (client check or the API's 400). */
  const [punchErr, setPunchErr] = useState<string | null>(null);
  const inRef = useRef<HTMLInputElement>(null);
  const outRef = useRef<HTMLInputElement>(null);

  const handleSave = async (close: boolean) => {
    const i = parseClock(inRaw);
    if (i == null) { setBad('in'); inRef.current?.focus(); return; }
    let outMinutes: number | null = null;
    if (outRaw.trim() !== '') {
      const o = parseClock(outRaw);
      if (o == null) { setBad('out'); outRef.current?.focus(); return; }
      // Earlier than the in → next local day; only up to 12:00 AM (sent as next-day 00:00).
      const out = resolveOutMinutes(i, o);
      if (out.crossesMidnight) {
        setPunchErr(PUNCH_CROSSES_MIDNIGHT_MESSAGE);
        setBad('out');
        outRef.current?.focus();
        return;
      }
      outMinutes = out.minutes;
    }

    const inUnchanged = isEdit && origIn != null && i === origIn.minutes;
    const outUnchanged = isEdit && outMinutes != null && origOutMinutes != null && outMinutes === origOutMinutes;

    const inStamp = inUnchanged
      ? { iso: entry!.inDateTime, offset: entry!.inDateTimeOffset }
      : wallTimeToTenantInstant(baseYmd, i);
    const outStamp = outMinutes == null
      ? null
      : outUnchanged
        ? { iso: entry!.outDateTime!, offset: entry!.outDateTimeOffset! }
        : wallTimeToTenantInstant(baseYmd, outMinutes);

    const body: PayrollPieceEntryBody = {
      ...(isEdit ? {} : { employeeID, payPeriodID }),
      inDateTime: inStamp.iso,
      inDateTimeOffset: inStamp.offset,
      outDateTime: outStamp?.iso ?? null,
      outDateTimeOffset: outStamp?.offset ?? null,
      type,
      editSessionID,
    };

    setSaving(true);
    setBanner(null);
    setPunchErr(null);
    const outcome = await performEntrySave('piece', isEdit ? entry!.timeEntryID : null, body);
    setSaving(false);
    if (!outcome.ok) {
      if (outcome.conflict) onConflict(outcome.message);
      else if (outcome.code === PUNCH_CROSSES_MIDNIGHT_CODE) { setPunchErr(outcome.message); setBad('out'); }
      else setBanner({ title: 'Could not save the piece punch.', body: outcome.message });
      return;
    }
    onSaved(outcome.result, close, { type });
  };

  return (
    <EntryModalShell
      kind="piece"
      mode={isEdit ? 'edit' : 'add'}
      subtitle={`${ymdLabel(baseYmd)} · ${employeeName}`}
      banner={banner ? <EntryErrorBanner title={banner.title} body={banner.body} /> : null}
      echo={<PunchEchoParts inRaw={inRaw} outRaw={outRaw} />}
      saving={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <p className="pe-note">
        Piece punch — no job costing. Payroll-created punches carry no GPS and no selfie.
      </p>
      <div className="row2">
        <div className="field">
          <label>Type</label>
          <select className="pe-in sans" value={type} onChange={(ev) => setType(Number(ev.target.value))}>
            <option value={TimeEntryType.Regular}>Regular</option>
            <option value={TimeEntryType.Meal}>Meal</option>
          </select>
        </div>
      </div>
      <div className="row2">
        <div className="field">
          <label>In</label>
          <input
            ref={inRef}
            className={`pe-in${bad === 'in' ? ' bad' : ''}`}
            value={inRaw}
            onChange={(ev) => { setInRaw(ev.target.value); setPunchErr(null); if (bad === 'in') setBad(null); }}
            placeholder="730"
            autoFocus
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <div className="field">
          <label>Out</label>
          <input
            ref={outRef}
            className={`pe-in${bad === 'out' ? ' bad' : ''}`}
            value={outRaw}
            onChange={(ev) => { setOutRaw(ev.target.value); setPunchErr(null); if (bad === 'out') setBad(null); }}
            placeholder="1500"
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </div>
      <PunchErrorSlot message={punchErr} />
    </EntryModalShell>
  );
};

export default PieceEntryModal;
